import { isClientProfile } from "@/config/uiConfig";
import type { DashboardRecord } from "@/lib/dashboard/types";

type Grants = NonNullable<DashboardRecord["audience_grants"]>;

/**
 * Whether a dashboard was published to a client: to its profile, to its account,
 * or to a tenant it reads. A data scope alone grants a client nothing.
 *
 * `tenantIds` are the tenants the account reads. Left out, a tenant grant is
 * taken as the server's answer (the API already lists only what the account may
 * open); given, it is checked, as when previewing another account.
 */
function publishedToClient(
  grants: Grants,
  profile: string | undefined,
  accountId: string | undefined,
  tenantIds: ReadonlySet<string> | undefined,
): boolean {
  return grants.some(
    (grant) =>
      (grant.grantee_type === "profile" && grant.grantee_key === profile) ||
      (grant.grantee_type === "account" &&
        accountId !== undefined &&
        grant.grantee_key === accountId) ||
      (grant.grantee_type === "tenant" &&
        (tenantIds === undefined || tenantIds.has(grant.grantee_key))),
  );
}

/**
 * Mirror of the backend's `can_read_dashboard` for profiles whose data scopes do
 * not, on their own, make a dashboard readable (`dashboards_via_scope = False`):
 * a client sees what it owns and what was published to its profile, its account
 * or a tenant it reads.
 *
 * The backend is the source of truth; this filter keeps client-side discovery
 * consistent with the configured account identity.
 */
export function isDashboardVisibleToProfile(
  dashboard: DashboardRecord,
  profile: string | undefined,
  accountId?: string,
  demoEnabled = false,
  tenantIds?: ReadonlySet<string>,
): boolean {
  if (demoEnabled && dashboard.status !== "active" && profile !== "nagra-admin") return false;
  if (dashboard.kind === "template" && isClientProfile(profile)) return false;
  if (!isClientProfile(profile)) return true;
  if (dashboard.access?.owned) return true;
  // Like the API: grants reach their audience once the dashboard is published.
  if (dashboard.state === "draft") return false;
  return publishedToClient(dashboard.audience_grants ?? [], profile, accountId, tenantIds);
}
